import "lib/polyfills";
import React from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { RouterProvider } from "react-router/dom";
import "./index.css";
import "views/style.css";
import "views/colors.css";
import "views/globalVars.css";
import registerServiceWorker from "./registerServiceWorker";
import store from "./state/store";
import { router } from "./router";
import { createRouterNavigationListener } from "./routes/routerNavigationListener";
import { createServiceWorkerNavigationListener } from "lib/serviceWorkerReloadHandler";
import errorMonitoring, {
  initializeGlobalErrorHandlers,
  createTestErrorHelper,
  ErrorMonitoringErrorBoundary
} from "lib/errorMonitoring";
import Copymaker from "lib/Copymaker";
import InputMode from "lib/InputMode";
import Render from "lib/Render";
import SSO from "lib/SSO";
import { default as ConfirmationModalLib } from "lib/ConfirmationModal/ConfirmationModal";
import { registerRenderCssAnimation } from "lib/renderCssAnimation";
import { registerRenderCaption } from "lib/renderCaption";
import { registerInjectDesignData } from "lib/injectDesignData";
import { registerVitePreloadErrorHandler } from "lib/vitePreloadErrorHandler";

registerVitePreloadErrorHandler();

// Register window APIs for server-side rendering (Puppeteer)
if (Render.isRenderMode()) {
  registerRenderCssAnimation();
  registerRenderCaption();
  registerInjectDesignData();
} else {
  SSO.init();

  ConfirmationModalLib.config({ store });

  // Initialize global error handlers for error monitoring.
  // Redux context is captured by the middleware and accessed by error handlers.
  initializeGlobalErrorHandlers(errorMonitoring);

  // Create test error helper for browser console testing.
  createTestErrorHelper(errorMonitoring);

  Copymaker.startModeIfNeeded();
  InputMode.startModeIfNeeded();
}

const Root = () => {
  return (
    <span className="notranslate">
      <Provider store={store}>
        <RouterProvider router={router} />
      </Provider>
    </span>
  );
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <ErrorMonitoringErrorBoundary action="Easil Web UI">
    <Root />
  </ErrorMonitoringErrorBoundary>
);

registerServiceWorker();

// Add event listeners on location change. This ensures that SPA navigation triggers a service
// worker update check like a browser navigation event would.
const serviceWorkerNavigationListener = createServiceWorkerNavigationListener({
  initialLocation: router.state.location,
  store,
  serviceWorker: navigator.serviceWorker,
  window
});
router.subscribe(
  createRouterNavigationListener({
    initialLocation: router.state.location,
    dispatch: store.dispatch,
    onNavigate: serviceWorkerNavigationListener
  })
);
